import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { cn } from "@/lib/utils";
import {
  fetchActivityTrend,
  fetchNpsByClient,
  fetchProgramsProgress,
} from "../api";
import { buildFiltersKey } from "../utils";
import type { AnalyticsFiltersState } from "../types";

interface AnalyticsChartsProps {
  filters: AnalyticsFiltersState;
}

function EmptyPlaceholder({
  message,
  variant = "light",
}: {
  message: string;
  variant?: "light" | "dark";
}) {
  const isDark = variant === "dark";
  return (
    <div
      className={cn(
        "flex h-full min-h-[160px] items-center justify-center rounded-md border text-sm",
        isDark
          ? "border-white/15 bg-white/5 text-white/80"
          : "border-dashed text-muted-foreground",
      )}
    >
      {message}
    </div>
  );
}

export function AnalyticsCharts({ filters }: AnalyticsChartsProps) {
  const windowDays = useMemo(() => {
    if (filters.dateRange.from && filters.dateRange.to) {
      const fromDate = new Date(`${filters.dateRange.from}T00:00:00`);
      const toDate = new Date(`${filters.dateRange.to}T00:00:00`);
      if (
        !Number.isNaN(fromDate.getTime()) &&
        !Number.isNaN(toDate.getTime())
      ) {
        const diffMs = toDate.getTime() - fromDate.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(1, diffDays);
      }
    }
    return 30;
  }, [filters.dateRange.from, filters.dateRange.to]);

  const dateFromParam = filters.dateRange.from ?? undefined;
  const dateToParam = filters.dateRange.to ?? undefined;

  const activityTrendQuery = useQuery({
    queryKey: ["analytics", "activityTrend", buildFiltersKey(filters)],
    queryFn: () =>
      fetchActivityTrend({
        days: windowDays,
        clienteIntermedioId: filters.clienteIntermedioId,
        clienteFinalId: filters.clienteFinalId,
        programaId: filters.programaId,
        actividadId: filters.actividadId,
        dateFrom: dateFromParam,
        dateTo: dateToParam,
      }),
  });

  const programsProgressQuery = useQuery({
    queryKey: ["analytics", "programsProgress", buildFiltersKey(filters)],
    queryFn: () =>
      fetchProgramsProgress({
        clienteIntermedioId: filters.clienteIntermedioId,
        clienteFinalId: filters.clienteFinalId,
        programaId: filters.programaId,
        actividadId: filters.actividadId,
        dateFrom: dateFromParam,
        dateTo: dateToParam,
      }),
  });

  const npsByClientQuery = useQuery({
    queryKey: ["analytics", "npsByClient", buildFiltersKey(filters)],
    queryFn: () =>
      fetchNpsByClient({
        windowDays,
        clienteIntermedioId: filters.clienteIntermedioId,
        clienteFinalId: filters.clienteFinalId,
        dateFrom: dateFromParam,
        dateTo: dateToParam,
      }),
  });

  const trendChartData = useMemo(() => {
    if (!activityTrendQuery.data) return [];
    return activityTrendQuery.data.map((row) => ({
      ...row,
      fecha: new Date(row.fecha).toLocaleDateString(),
    }));
  }, [activityTrendQuery.data]);

  const programsChartData = useMemo(
    () => (programsProgressQuery.data ?? []).slice(0, 12),
    [programsProgressQuery.data],
  );

  const npsChartData = useMemo(
    () => (npsByClientQuery.data ?? []).slice(0, 10),
    [npsByClientQuery.data],
  );

  return (
    <>
      <section className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-3">
        <Card className="relative overflow-hidden border-0 bg-slate-950 text-slate-100 shadow-[0_30px_90px_-45px_rgba(14,116,173,0.65)] lg:col-span-2">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/70 to-slate-950/95" />
          <div className="absolute -right-14 top-10 h-40 w-40 rounded-full bg-sky-500/25 blur-3xl" />
          <CardHeader className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6 gap-2">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base font-semibold text-slate-100">
              Actividad (Intentos por día)
            </CardTitle>
            <span className="rounded-full bg-white/10 px-2 sm:px-3 py-0.5 sm:py-1 text-[0.7rem] sm:text-xs text-slate-100/80 backdrop-blur">
              Tendencia diaria
            </span>
          </CardHeader>
          <CardContent className="relative min-h-[250px] sm:min-h-[300px] px-2 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
            {activityTrendQuery.isLoading ? (
              <Skeleton className="h-full w-full bg-slate-800/40" />
            ) : activityTrendQuery.error ? (
              <EmptyPlaceholder
                message={activityTrendQuery.error.message}
                variant="dark"
              />
            ) : trendChartData.length === 0 ? (
              <EmptyPlaceholder
                message="Sin actividad registrada en el periodo seleccionado."
                variant="dark"
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendChartData}>
                  <CartesianGrid
                    stroke="rgba(148,163,184,0.25)"
                    strokeDasharray="4 8"
                  />
                  <XAxis
                    dataKey="fecha"
                    tick={{ fill: "rgba(226,232,240,0.75)", fontSize: 12 }}
                    stroke="transparent"
                  />
                  <YAxis
                    tick={{ fill: "rgba(226,232,240,0.75)", fontSize: 12 }}
                    stroke="transparent"
                  />
                  <Tooltip
                    cursor={{ stroke: "rgba(56,189,248,0.35)", strokeWidth: 2 }}
                    contentStyle={{
                      backgroundColor: "rgba(8,47,73,0.92)",
                      border: "1px solid rgba(125,211,252,0.35)",
                      borderRadius: 12,
                      color: "#e2e8f0",
                    }}
                    labelStyle={{ color: "#bae6fd", fontWeight: 600 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="intentos"
                    stroke="#38bdf8"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6, fill: "#38bdf8", strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 bg-slate-950 text-slate-100 shadow-[0_30px_90px_-45px_rgba(147,51,234,0.6)]">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/95 via-slate-900/80 to-slate-950/95" />
          <div className="absolute -left-14 bottom-8 h-36 w-36 rounded-full bg-fuchsia-500/25 blur-3xl" />
          <CardHeader className="relative flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-100">
              NPS por cliente
            </CardTitle>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-slate-100/80 backdrop-blur">
              Top 10
            </span>
          </CardHeader>
          <CardContent className="relative min-h-[300px]">
            {npsByClientQuery.isLoading ? (
              <Skeleton className="h-full w-full bg-slate-800/40" />
            ) : npsByClientQuery.error ? (
              <EmptyPlaceholder
                message={npsByClientQuery.error.message}
                variant="dark"
              />
            ) : npsChartData.length === 0 ? (
              <EmptyPlaceholder
                message="Sin respuestas NPS en el periodo."
                variant="dark"
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={npsChartData}
                  layout="vertical"
                  margin={{ left: 40, right: 16, top: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    stroke="rgba(196,181,253,0.15)"
                    strokeDasharray="4 6"
                  />
                  <XAxis
                    type="number"
                    domain={[-100, 100]}
                    tick={{ fill: "rgba(233,213,255,0.8)", fontSize: 11 }}
                    stroke="transparent"
                  />
                  <YAxis
                    type="category"
                    dataKey="nombre_empresa"
                    width={160}
                    tick={{ fill: "rgba(233,213,255,0.85)", fontSize: 12 }}
                    stroke="transparent"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(88,28,135,0.92)",
                      border: "1px solid rgba(233,213,255,0.3)",
                      borderRadius: 12,
                      color: "#f5f3ff",
                    }}
                    labelStyle={{ color: "#f5f3ff", fontWeight: 600 }}
                  />
                  <Legend
                    wrapperStyle={{
                      color: "rgba(233,213,255,0.9)",
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="nps" name="NPS" radius={8} fill="#c084fc" />
                  <Bar
                    dataKey="total"
                    name="Respuestas"
                    radius={8}
                    fill="#fcd34d"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="relative overflow-hidden border-0 bg-slate-950 text-slate-100 shadow-[0_35px_100px_-55px_rgba(34,197,94,0.6)] lg:col-span-2">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/95 via-slate-900/75 to-slate-950/95" />
          <div className="absolute -right-16 bottom-0 h-40 w-40 rounded-full bg-emerald-400/25 blur-3xl" />
          <CardHeader className="relative flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-100">
              Progreso por programa
            </CardTitle>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-slate-100/80 backdrop-blur">
              Programas activos
            </span>
          </CardHeader>
          <CardContent className="relative min-h-[300px]">
            {programsProgressQuery.isLoading ? (
              <Skeleton className="h-full w-full bg-slate-800/40" />
            ) : programsProgressQuery.error ? (
              <EmptyPlaceholder
                message={programsProgressQuery.error.message}
                variant="dark"
              />
            ) : programsChartData.length === 0 ? (
              <EmptyPlaceholder
                message="Sin programas con progreso en el periodo."
                variant="dark"
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={programsChartData}
                  margin={{ top: 8, right: 16, bottom: 8, left: 16 }}
                >
                  <CartesianGrid
                    stroke="rgba(134,239,172,0.2)"
                    strokeDasharray="4 6"
                  />
                  <XAxis
                    dataKey="nombre"
                    interval={0}
                    angle={-12}
                    textAnchor="end"
                    height={70}
                    tick={{
                      fill: "rgba(209,250,229,0.8)",
                      fontSize: 11,
                    }}
                    stroke="transparent"
                  />
                  <YAxis
                    tick={{ fill: "rgba(209,250,229,0.8)", fontSize: 12 }}
                    stroke="transparent"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(6,78,59,0.92)",
                      border: "1px solid rgba(134,239,172,0.35)",
                      borderRadius: 12,
                      color: "#dcfce7",
                    }}
                    labelStyle={{ color: "#dcfce7", fontWeight: 600 }}
                  />
                  <Legend
                    wrapperStyle={{
                      color: "rgba(209,250,229,0.9)",
                      fontSize: 12,
                    }}
                  />
                  <Bar
                    dataKey="avgProgreso"
                    name="Promedio %"
                    radius={10}
                    fill="#34d399"
                  />
                  <Bar
                    dataKey="tasaFin"
                    name="% Finalización"
                    radius={10}
                    fill="#22d3ee"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </section>
    </>
  );
}
